// Generic "show more / show less" for any list clamped by CSS max-height.
// The collapsed clamp is a CSS class so the initial state is right before JS
// runs; this only measures scrollHeight to animate open, swaps the label, and
// hides the toggle when the content fits anyway.
//
//   <div data-controller="collapsible"
//        data-collapsible-collapsed-label-value="Show all 18 skills"
//        data-collapsible-expanded-label-value="Show less">
//     <div class="skills-wrap collapsed" data-collapsible-target="content">…</div>
//     <button data-collapsible-target="toggle" data-action="click->collapsible#toggle">
//       <span data-collapsible-target="label">Show all 18 skills</span>
//     </button>
//   </div>
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["content", "toggle", "label"];

  static values = {
    collapsedLabel: String,
    expandedLabel: String,
    collapsedClass: String,
  };

  declare readonly contentTarget: HTMLElement;
  declare readonly hasToggleTarget: boolean;
  declare readonly toggleTarget: HTMLElement;
  declare readonly hasLabelTarget: boolean;
  declare readonly labelTarget: HTMLElement;

  declare readonly collapsedLabelValue: string;
  declare readonly expandedLabelValue: string;
  declare readonly collapsedClassValue: string;

  connect(): void {
    // A list that already fits needs no toggle — and must not stay clamped.
    if (!this.overflows()) {
      this.contentTarget.classList.remove(this.klass);
      if (this.hasToggleTarget) this.toggleTarget.style.display = "none";
    }
  }

  toggle(): void {
    const collapsed = this.contentTarget.classList.toggle(this.klass);

    if (collapsed) {
      this.contentTarget.style.maxHeight = ""; // hand the clamp back to CSS
    } else {
      this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`;
    }

    if (this.hasToggleTarget) this.toggleTarget.classList.toggle("open", !collapsed);
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = collapsed ? this.collapsedLabelValue : this.expandedLabelValue;
    }
  }

  private get klass(): string {
    return this.collapsedClassValue || "collapsed";
  }

  // scrollHeight exceeding clientHeight means the clamp is actually hiding rows
  private overflows(): boolean {
    const el = this.contentTarget;
    if (!el.classList.contains(this.klass)) return true;
    return el.scrollHeight > el.clientHeight + 1;
  }
}
